import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Center,
  Container,
  Spinner,
  Button,
  HStack,
  Text,
  Spacer,
  Box,
  ListItem,
  ListIcon,
  List,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  VStack,
  Heading,
  Badge,
} from "@chakra-ui/react";
import { Context } from "../../ContextWrapper";
import { useCurrentProfile } from "../../ContextWrapper";
import { DropDown } from "../generic/dropDown";
import { FaLink, FaUnlink } from "react-icons/fa";
import {
  fetchAccountSubTypes,
  fetchAccountTaxTypes,
  fetchAccountTypes,
  fetchCustomerAccounts,
} from "../../api/accounts";
import { AccountModal } from "../account/chartOfAccounts";
import { QuickBooksSyncModal } from "../shared/QuickBooksSyncModal";
import { TB_SYNC_DATES } from "../../config/syncDefaults";
import { ValidationResultModal } from "../shared/ValidationResultModal";
import { quickbooksService } from "../../api/quickbooks";

export const ClientQuickbooks = ({ customer_id }) => {
  const [qbLinked, setQbLinked] = useState(false);
  const [missingMainAccounts, setMissingMainAccounts] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [authUrl, setAuthUrl] = useState("");
  const [extraAccounts, setExtraAccounts] = useState([]);
  const [renumberAccounts, setRenumberAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingSync, setLoadingSync] = useState(false);
  const [loadingRenumber, setLoadingRenumber] = useState(false);
  const [loadingRestructure, setLoadingRestructure] = useState(false);
  const [errors, setErrors] = useState([]);
  const [failedJobData, setFailedJobData] = useState(null);
  const [customer, setCustomer] = useState(null); // Customer data for modal
  const {
    isOpen: isExportOpen,
    onOpen: onExportOpen,
    onClose: onExportClose,
  } = useDisclosure();
  const {
    isOpen: isSyncOpen,
    onOpen: onSyncOpen,
    onClose: onSyncClose,
  } = useDisclosure();
  const {
    isOpen: isRenumberOpen,
    onOpen: onRenumberOpen,
    onClose: onRenumberClose,
  } = useDisclosure();
  const {
    isOpen: isRestructureOpen,
    onOpen: onRestructureOpen,
    onClose: onRestructureClose,
  } = useDisclosure();
  const {
    isOpen: isErrorOpen,
    onOpen: onErrorOpen,
    onClose: onErrorClose,
  } = useDisclosure();
  const {
    isOpen: isAccountantConfirmOpen,
    onOpen: onAccountantConfirmOpen,
    onClose: onAccountantConfirmClose,
  } = useDisclosure();
  const {
    isOpen: isOpeningBalanceModalOpen,
    onOpen: onOpeningBalanceModalOpen,
    onClose: onOpeningBalanceModalClose,
  } = useDisclosure();
  const {
    isOpen: isValidationModalOpen,
    onOpen: openValidationModal,
    onClose: closeValidationModal,
  } = useDisclosure();
  const [openingBalanceInput, setOpeningBalanceInput] = useState("");
  const [updatingOpeningBalance, setUpdatingOpeningBalance] = useState(false);
  const [validationCustomer, setValidationCustomer] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const validationPollTimeout = useRef(null);
  const context = React.useContext(Context);
  const { profile } = useCurrentProfile();

  // Determine if user is admin for role-based features
  const isAdmin = profile?.type === "admin";


  useEffect(() => {
    if (!customer_id) {
      return;
    }

    loadQuickbooksAccounts(customer_id);
    loadCustomerData(customer_id);
  }, [customer_id]);

  const loadCustomerData = (id) => {
    axios
      .get(
        `/api/v2/customers/${id}/`,
        { headers: { "Content-Type": "application/json" } },
        { withCredentials: true }
      )
      .then((res) => {
        setCustomer(res.data);
      })
      .catch((err) => console.log(err));
  };

  const loadQuickbooksAccounts = (id, setChildLoading) => {
    axios
      .get(
        `/api/v2/client_quickbooks_accounts/${id}/`,
        { headers: { "Content-Type": "application/json" } },
        { withCredentials: true }
      )
      .then((res) => {
        setQbLinked(res.data.qb_linked);
        setMissingMainAccounts(res.data.missing_main_accounts);
        setAccounts(res.data.accounts);
        setAuthUrl(res.data.auth_url);
        setExtraAccounts(res.data.extra_qb_accounts);
        setRenumberAccounts(res.data.renumber_qb_accounts);

        sessionStorage.setItem("customer_id", id);

        setLoading(false);
        if (setChildLoading) {
          setChildLoading(false);
        }
      })
      .catch((err) => console.log(err));
  };

  const disconnectQuickbooks = () => {
    setLoading(true);
    axios
      .post(
        `/api/v2/client_link_quickbooks/`,
        {
          customer_id: customer_id,
          action: "disconnect",
        },
        { headers: { "Content-Type": "application/json" } },
        { withCredentials: true }
      )
      .then(() => {
        setQbLinked(false);
        setLoading(false);
      })
      .catch((err) => console.log(err));
  };

  const createMainAccounts = () => {
    setLoading(true);
    axios
      .post(
        `/api/v2/create_main_qb_account/`,
        {
          customer_id: customer_id,
        },
        { headers: { "Content-Type": "application/json" } },
        { withCredentials: true }
      )
      .then(() => {
        loadQuickbooksAccounts(customer_id);
      })
      .catch((err) => {
        context.fireToast("Error", err.response.data.error, "error");
        setLoading(false);
        console.log(err);
      });
  };

  const exportTransactions = (startDate, endDate) => {
    setLoadingExport(true);
    axios
      .post(
        `/api/v2/export_quickbooks_transactions/${customer_id}/`,
        {
          start_date: startDate,
          end_date: endDate,
        },
        { headers: { "Content-Type": "application/json" } },
        { withCredentials: true }
      )
      .then((response) => {
        const link = document.createElement("a");
        link.href = response.data.file;
        link.setAttribute("download", response.data.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        onExportClose();
        setLoadingExport(false);
      })
      .catch((err) => console.log(err));
  };

  // Refs for tracking async sync jobs and polling
  const pollingTimeoutRef = useRef(null);
  const currentJobRef = useRef(null);

  // Helper function to extract errors from job results
  const extractJobErrors = (jobData) => {
    const allErrors = [];
    if (jobData.results) {
      Object.values(jobData.results).forEach(customerResult => {
        Object.values(customerResult).forEach(activity => {
          if (activity.status === 'failed' && activity.errors) {
            if (Array.isArray(activity.errors)) {
              allErrors.push(...activity.errors);
            }
          }
        });
      });
    }
    return allErrors;
  };

  // Polling function to check job status
  const pollJobStatus = async (jobId) => {
    try {
      console.log('Polling job status for:', jobId);
      const response = await quickbooksService.getJobDetails(jobId);
      const jobData = response.data;
      
      console.log('Job status poll result:', jobData.status, jobData);
      
      // Check if job is explicitly completed or failed
      if (jobData.status === 'completed' || jobData.status === 'failed') {
        console.log('Job finished with status:', jobData.status);
        // Reset loading state when job completes
        setLoadingSync(false);
        currentJobRef.current = null;
        onSyncClose(); // Close the modal when job completes
        
        // Show completion/failure notification
        if (jobData.status === 'completed') {
          context.fireToast(
            "Success",
            "QuickBooks sync completed successfully.",
            "success"
          );
        } else {
          // Extract and show detailed errors
          const jobErrors = extractJobErrors(jobData);
          if (jobErrors.length > 0) {
            setErrors(jobErrors);
            setFailedJobData(jobData);
            onErrorOpen();
          } else {
            context.fireToast(
              "Error",
              `QuickBooks sync failed: ${jobData.error || 'Unknown error'}`,
              "error"
            );
          }
        }
        return true; // Stop polling
      }
      
      // Check if all activities are completed (workaround for backend not updating workflow status)
      if (jobData.results && Object.keys(jobData.results).length > 0) {
        const allActivitiesCompleted = Object.values(jobData.results).every(customerResult => {
          // Check all activity types in the customer result
          return Object.values(customerResult).every(activity => {
            return activity.status === 'completed' || activity.status === 'failed';
          });
        });
        
        if (allActivitiesCompleted) {
          console.log('All activities completed, treating job as finished');
          // Check if any activities failed
          const anyFailures = Object.values(jobData.results).some(customerResult => {
            return Object.values(customerResult).some(activity => {
              return activity.status === 'failed' || !activity.success;
            });
          });
          
          setLoadingSync(false);
          currentJobRef.current = null;
          onSyncClose();
          
          if (anyFailures) {
            // Extract and show detailed errors
            const jobErrors = extractJobErrors(jobData);
            if (jobErrors.length > 0) {
              setErrors(jobErrors);
              setFailedJobData(jobData);
              onErrorOpen();
            } else {
              context.fireToast(
                "Warning",
                "QuickBooks sync completed with some errors. Please check the results.",
                "warning"
              );
            }
          } else {
            context.fireToast(
              "Success",
              "QuickBooks sync completed successfully.",
              "success"
            );
          }
          return true; // Stop polling
        }
      }
      
      console.log('Job still in progress, continuing to poll...');
      return false; // Continue polling
    } catch (error) {
      console.error('Error polling job status:', error);
      // Continue polling on error unless it's a 404
      if (error.response && error.response.status === 404) {
        console.log('Job not found (404), stopping polling');
        setLoadingSync(false);
        currentJobRef.current = null;
        onSyncClose(); // Close modal on 404
        context.fireToast(
          "Error",
          "Sync job not found. The sync may have completed.",
          "warning"
        );
        return true; // Stop polling
      }
      console.log('Polling error, but continuing:', error.message);
      return false; // Continue polling
    }
  };

  // Start polling for job completion
  const startJobPolling = (jobId) => {
    console.log('Starting polling for job:', jobId);
    currentJobRef.current = jobId;
    
    // Clear any existing polling
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
    
    let pollCount = 0;
    const maxPolls = 60; // Maximum 3 minutes of polling (60 * 3 seconds)
    
    const poll = async () => {
      // Check if we should still be polling for this job
      if (currentJobRef.current !== jobId) {
        console.log('Stopping polling - job changed');
        return;
      }
      
      pollCount++;
      console.log(`Poll attempt ${pollCount}/${maxPolls} for job ${jobId}`);
      
      // Safety timeout - stop polling after max attempts
      if (pollCount >= maxPolls) {
        console.log('Polling timeout reached, stopping');
        setLoadingSync(false);
        currentJobRef.current = null;
        onSyncClose();
        context.fireToast(
          "Warning",
          "Sync job is taking longer than expected. Please check the sync history.",
          "warning"
        );
        return;
      }
      
      const shouldStop = await pollJobStatus(jobId);
      if (!shouldStop && currentJobRef.current === jobId) {
        pollingTimeoutRef.current = setTimeout(poll, 3000); // Poll every 3 seconds
      }
    };
    
    // Start polling after a short delay
    pollingTimeoutRef.current = setTimeout(poll, 2000);
  };

  // Cleanup polling when component unmounts or job changes
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
      currentJobRef.current = null;
      if (validationPollTimeout.current) {
        clearTimeout(validationPollTimeout.current);
        validationPollTimeout.current = null;
      }
    };
  }, []);

  // Unified sync function using the new API
  const handleSyncConfirmed = async (syncParams) => {
    setLoadingSync(true);
    // Don't close modal yet - we'll close it based on sync type

    try {
      const response = await quickbooksService.syncQuickbooksData(syncParams);
      
      if (response.data.errors && response.data.errors.length > 0) {
        setErrors(response.data.errors);
        onErrorOpen();
        setLoadingSync(false);
        onSyncClose(); // Close modal on error
      } else {
        // Check if this was a synchronous CDC sync that completed immediately
        if (response.data.completed_sync) {
          context.fireToast(
            "Success",
            response.data.message || "QuickBooks sync completed successfully.",
            "success"
          );
          setLoadingSync(false);
          onSyncClose(); // Close modal for completed sync
        } else {
          // Asynchronous sync - job started, keep modal open with spinner
          context.fireToast(
            "Success",
            response.data.message || "QuickBooks sync has been started successfully.",
            "success"
          );
          
          // Start polling for job completion if job_id is provided
          if (response.data.job_id) {
            startJobPolling(response.data.job_id);
            // Modal stays open with loading spinner
          } else {
            // Fallback: reset loading state and close modal if no job ID
            setTimeout(() => {
              setLoadingSync(false);
              onSyncClose();
            }, 5000);
          }
        }
      }
    } catch (err) {
      console.error("Sync error:", err);
      if (err.response && err.response.data) {
        if (err.response.data.non_field_errors) {
          err.response.data.non_field_errors.forEach(errObj => {
            context.fireToast("Error", errObj.message || errObj, "error");
          });
        } else if (err.response.data.error) {
          // Handle synchronous sync errors
          context.fireToast("Error", err.response.data.error, "error");
        } else {
          context.fireToast("Error", "An unknown error occurred", "error");
        }
      } else {
        context.fireToast("Error", "An unknown error occurred", "error");
      }
      setLoadingSync(false);
      onSyncClose(); // Close modal on error
    }
  };

  const openExport = () => {
    onExportOpen();
    setLoadingExport(false);
  };

  const openSync = () => {
    onSyncOpen();
    setLoadingSync(false);
  };

  const renumberQBAccounts = (qb_accounts) => {
    setLoadingRenumber(true);
    axios
      .post(
        `/api/v2/renumber_qb_accounts/`,
        {
          customer_id: customer_id,
          qb_accounts: qb_accounts,
        },
        { headers: { "Content-Type": "application/json" } },
        { withCredentials: true }
      )
      .then((res) => {
        if (res.status < 300) {
          setLoading(true);
          loadQuickbooksAccounts(customer_id, setLoading);
        } else {
          context.fireToast("Error", res.response.data.error, "error");
        }
        setLoadingRenumber(false);
        onRenumberClose();
      })
      .catch((err) => {
        context.fireToast("Error", err.response.data.error, "error");
        setLoadingRenumber(false);
        onRenumberClose();
      });
  };

  const restructureAccounts = (qb_accounts) => {
    setLoadingRestructure(true);
    axios
      .post(
        `/api/v2/restructure_qb_accounts/${customer_id}/`,
        {
          qb_account_ids: qb_accounts,
        },
        { headers: { "Content-Type": "application/json" } },
        { withCredentials: true }
      )
      .then((res) => {
        if (Object.keys(res.data.errors).length === 0) {
          setLoading(true);
          loadQuickbooksAccounts(customer_id, setLoading);
        } else {
          setErrors(res.data.errors);
          onErrorOpen();
        }
        setLoadingRestructure(false);
        onRestructureClose();
      })
      .catch((err) => {
        context.fireToast("Error", err.response.data.error, "error");
        setLoadingRestructure(false);
        onRestructureClose();
      });
  };

  const openRenumber = () => {
    onRenumberOpen();
    setLoadingRenumber(false);
  };

  const openRestructure = () => {
    onRestructureOpen();
    setLoadingRestructure(false);
  };

  const handleOpeningBalanceModalOpen = () => {
    const defaultValue =
      customer?.opening_balance_date || TB_SYNC_DATES.startDate || "";
    setOpeningBalanceInput(defaultValue);
    onOpeningBalanceModalOpen();
  };

  const handleSaveOpeningBalanceDate = (value) => {
    setUpdatingOpeningBalance(true);
    axios
      .patch(
        `/api/v2/customers/${customer_id}/`,
        { opening_balance_date: value || null },
        { headers: { "Content-Type": "application/json" } }
      )
      .then((res) => {
        context.fireToast(
          "Success",
          value
            ? "Opening balance date updated."
            : "Opening balance date reset to default.",
          "success"
        );
        if (res.data) {
          setCustomer(res.data);
          setOpeningBalanceInput(
            res.data?.opening_balance_date || TB_SYNC_DATES.startDate || ""
          );
        } else {
          loadCustomerData(customer_id);
          setOpeningBalanceInput(value || TB_SYNC_DATES.startDate || "");
        }
        onOpeningBalanceModalClose();
      })
      .catch((err) => {
        console.error(err);
        const message =
          err.response?.data?.opening_balance_date ||
          err.response?.data?.error ||
          "Failed to update opening balance date.";
        context.fireToast("Error", message, "error");
      })
      .finally(() => {
        setUpdatingOpeningBalance(false);
      });
  };

  const handleSubmitOpeningBalanceDate = (event) => {
    event.preventDefault();
    handleSaveOpeningBalanceDate(openingBalanceInput || null);
  };

  const handleCloseValidationModal = () => {
    closeValidationModal();
    setValidationCustomer(null);
    setValidationResults(null);
    setValidationLoading(false);
    if (validationPollTimeout.current) {
      clearTimeout(validationPollTimeout.current);
      validationPollTimeout.current = null;
    }
  };

  const isValidationPending = (syncStatus) => {
    if (!syncStatus) return false;
    const lastSyncSuccess =
      syncStatus.gl_last_sync_success || syncStatus.gl_last_sync_attempt;
    if (!lastSyncSuccess) return false;
    const validationAttempt = syncStatus.last_validation_attempt;
    if (!validationAttempt) return true;
    return new Date(validationAttempt) < new Date(lastSyncSuccess);
  };

  const scheduleValidationRefresh = (customerId) => {
    if (validationPollTimeout.current) {
      clearTimeout(validationPollTimeout.current);
    }
    validationPollTimeout.current = setTimeout(async () => {
      try {
        const response = await quickbooksService.getCustomerById(customerId, {
          include_quickbooks_sync_info: true,
          include_quickbooks_sync_results: true,
          include_quickbooks_connection: true,
        });

        const refreshed = response.data;
        setValidationCustomer(refreshed);
        setValidationResults(
          refreshed?.sync_statuses?.last_validation_results || null
        );

        if (isValidationPending(refreshed?.sync_statuses)) {
          scheduleValidationRefresh(customerId);
        } else {
          setValidationLoading(false);
          if (validationPollTimeout.current) {
            clearTimeout(validationPollTimeout.current);
            validationPollTimeout.current = null;
          }
        }
      } catch (error) {
        console.error("Error refreshing validation results:", error);
        setValidationLoading(false);
        if (validationPollTimeout.current) {
          clearTimeout(validationPollTimeout.current);
          validationPollTimeout.current = null;
        }
      }
    }, 4000);
  };

  const handleViewValidation = async () => {
    if (!customer?.id) {
      return;
    }

    setValidationCustomer(customer);
    setValidationResults(null);
    setValidationLoading(true);
    openValidationModal();

    try {
      const response = await quickbooksService.getCustomerById(customer.id, {
        include_quickbooks_sync_info: true,
        include_quickbooks_sync_results: true,
        include_quickbooks_connection: true,
      });

      const data = response.data;
      setValidationCustomer(data);
      setValidationResults(
        data?.sync_statuses?.last_validation_results || null
      );
      if (isValidationPending(data?.sync_statuses)) {
        scheduleValidationRefresh(customer.id);
      } else {
        setValidationLoading(false);
      }
    } catch (error) {
      console.error("Error loading validation results:", error);
      context.fireToast(
        "Validation Results",
        "Unable to load validation report.",
        "error"
      );
      handleCloseValidationModal();
    }
  };

  return (
    <Container maxW="100%" height="calc(100vh - 80px)" bg="brand.50">
      <Center pt="8">
        {loading && <Spinner color="brand.600" />}
        {!qbLinked && !loading && (
          <Button
            onClick={() => {
              sessionStorage.setItem("qb_connect_origin", "client");
              sessionStorage.setItem("qb_connect_page_params", window.location.search || "");
              window.location.href = authUrl;
            }}
          >
            Connect Quickbooks
          </Button>
        )}
      </Center>

      {qbLinked && !loading && (
        <>
          <ExportModal
            isOpen={isExportOpen}
            onClose={onExportClose}
            exportTransactions={exportTransactions}
            loading={loadingExport}
          />
          <QuickBooksSyncModal
            isOpen={isSyncOpen}
            onClose={onSyncClose}
            customer={customer}
            onSyncConfirmed={handleSyncConfirmed}
            isAdmin={isAdmin}
            isLoading={loadingSync}
          />
          <ValidationResultModal
            isOpen={isValidationModalOpen}
            onClose={handleCloseValidationModal}
            customer={validationCustomer}
            results={validationResults}
            isLoading={validationLoading}
          />
          <RenumberModal
            isOpen={isRenumberOpen}
            onClose={onRenumberClose}
            renumberQBAccounts={renumberQBAccounts}
            loading={loadingRenumber}
            accounts={renumberAccounts}
          />
          {isRestructureOpen && (
            <RestructureModal
              isOpen={isRestructureOpen}
              onClose={onRestructureClose}
              restructureAccounts={restructureAccounts}
              loading={loadingRestructure}
              customer_id={customer_id}
            />
          )}
          <ErrorModal
            isOpen={isErrorOpen}
            onClose={() => {
              onErrorClose();
              setFailedJobData(null);
            }}
            errors={errors}
            jobData={failedJobData}
            customerId={customer_id}
          />
          <Modal
            isOpen={isOpeningBalanceModalOpen}
            onClose={() => {
              if (!updatingOpeningBalance) {
                onOpeningBalanceModalClose();
              }
            }}
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Opening Balance Date</ModalHeader>
              <ModalCloseButton isDisabled={updatingOpeningBalance} />
              <form onSubmit={handleSubmitOpeningBalanceDate}>
                <ModalBody>
                  <VStack align="stretch" spacing={4}>
                    <Text color="gray.600" fontSize="sm">
                      Choose the date that should be treated as the opening balance cutoff
                      for this customer. The trial balance sync will use this date and
                      general ledger imports will start the following day.
                    </Text>
                    <FormControl>
                      <FormLabel>Opening Balance Date</FormLabel>
                      <Input
                        type="date"
                        value={openingBalanceInput || ""}
                        onChange={(event) =>
                          setOpeningBalanceInput(event.target.value)
                        }
                      />
                    </FormControl>
                  </VStack>
                </ModalBody>
                <ModalFooter>
                  <HStack spacing={3}>
                    <Button
                      variant="ghost"
                      onClick={() => onOpeningBalanceModalClose()}
                      isDisabled={updatingOpeningBalance}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleSaveOpeningBalanceDate(null)}
                      isDisabled={updatingOpeningBalance}
                    >
                      Reset to Default
                    </Button>
                    <Button
                      colorScheme="brand"
                      type="submit"
                      isLoading={updatingOpeningBalance}
                    >
                      Save
                    </Button>
                  </HStack>
                </ModalFooter>
              </form>
            </ModalContent>
          </Modal>
          {/* Confirmation modal for accountants */}
          <Modal isOpen={isAccountantConfirmOpen} onClose={onAccountantConfirmClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Sync QuickBooks Data</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <Text>
                    This will synchronize recent transactions and account changes from QuickBooks.
                  </Text>
                  <Text color="gray.600" fontSize="sm">
                    Only new or updated data since your last sync will be imported. Existing records remain unchanged.
                  </Text>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button variant="outline" mr={3} onClick={onAccountantConfirmClose}>
                  Cancel
                </Button>
                <Button 
                  colorScheme="blue" 
                  onClick={() => {
                    onAccountantConfirmClose();
                    handleSyncConfirmed({ 
                      syncType: 'transactions', 
                      fetchMode: 'cdc', 
                      importMode: 'import', 
                      customerId: customer_id 
                    });
                  }}
                  isLoading={loadingSync}
                >
                  Confirm Sync
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
          <Center>
            <HStack spacing={3}>
              <Button onClick={disconnectQuickbooks}>
                Disconnect Quickbooks
              </Button>
              {missingMainAccounts && (
                <Button onClick={createMainAccounts}>
                  Create main accounts
                </Button>
              )}
              {renumberAccounts && (
                <Button onClick={openRenumber}>Renumber QB accounts</Button>
              )}
              <Button onClick={openRestructure}>Restructure QB accounts</Button>
              <Button onClick={openExport}>Export Transactions</Button>
              <Button onClick={handleOpeningBalanceModalOpen} isDisabled={!customer}>
                Opening Balance Date
              </Button>
              {/* Role-based UI: Admin gets modal, accountant gets confirmation then simple sync */}
              {isAdmin ? (
                <Button onClick={openSync} colorScheme="brand">
                  Sync QuickBooks
                </Button>
              ) : (
                <Button
                  onClick={onAccountantConfirmOpen}
                  colorScheme="brand"
                  isLoading={loadingSync}
                >
                  Sync Changes
                </Button>
              )}
              <Button
                onClick={handleViewValidation}
                isDisabled={!customer || validationLoading}
                isLoading={validationLoading}
              >
                Sync Status
              </Button>
            </HStack>
          </Center>
          <Box
            maxHeight="calc(100vh - 184px)"
            p="8"
            m="8"
            bg="white"
            overflowX="scroll"
          >
            <Text fontSize="xl" pb={3}>
              Sync accounts to Quickbooks
            </Text>
            {accounts && (
              <List>
                {accounts.map((account) => (
                  <QuickbooksAccount
                    key={account.id}
                    account={account}
                    customer_id={customer_id}
                    loadQuickbooksAccounts={loadQuickbooksAccounts}
                  />
                ))}
              </List>
            )}
            <CreateLocalAccounts
              customer_id={customer_id}
              accounts={extraAccounts}
              setAccounts={setExtraAccounts}
            />
          </Box>
        </>
      )}
    </Container>
  );
};

const CreateLocalAccounts = ({ customer_id, accounts, setAccounts }) => {
  const [accountList, setAccountList] = useState([]);
  const [accountTypeList, setAccountTypeList] = useState([]);
  const [accountSubTypeList, setAccountSubTypeList] = useState([]);
  const [accountTaxTypeList, setAccountTaxTypeList] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentAccount, setCurrentAccount] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const context = React.useContext(Context);

  useEffect(() => {
    const getData = async () => {
      const accountResults = fetchCustomerAccounts(customer_id);
      const typeResults = fetchAccountTypes();
      const subTypeResults = fetchAccountSubTypes();
      const taxTypeResults = fetchAccountTaxTypes();

      setAccountList(await accountResults);
      setAccountTypeList(await typeResults);
      setAccountSubTypeList(await subTypeResults);
      setAccountTaxTypeList(await taxTypeResults);
    };
    getData();
  }, [customer_id]);

  const openModal = (account) => {
    setCurrentAccount({ name: account.name, number: account.number });
    setFieldErrors({});
    onOpen();
  };

  const closeModal = (account) => {
    setCurrentAccount({});
    setFieldErrors({});
    onClose();
  };

  const handleSubmit = async (event, account) => {
    event.preventDefault();
    setFieldErrors({});

    axios
      .post(
        `/api/v2/customers/${customer_id}/accounts/`,
        account,
        { headers: { "Content-Type": "application/json" } },
        { withCredentials: true }
      )
      .then((res) => {
        setAccounts(accounts.filter((a) => a.number !== res.data.number));
        setCurrentAccount({});
        setFieldErrors({});
        onClose();
      })
      .catch((err) => {
        if (err.response && err.response.data) {
          const data = err.response.data;
          let newFieldErrors = {};
          ["name", "number", "type"].forEach(field => {
            if (data[field]) {
              newFieldErrors[field] = Array.isArray(data[field]) ? data[field] : [data[field]];
            }
          });
          setFieldErrors(newFieldErrors);
          // Show toast only for truly unknown/general errors
          if (!newFieldErrors.name && !newFieldErrors.number && !newFieldErrors.type) {
            context.fireToast("Error", data.detail || "An unknown error occurred", "error");
          }
        } else {
          context.fireToast("Error", "An unknown error occurred", "error");
        }
      });
  };

  return (
    <>
      <AccountModal
        isOpen={isOpen}
        toggle={closeModal}
        account={currentAccount}
        accountList={accountList}
        accountTypeList={accountTypeList}
        accountSubTypeList={accountSubTypeList}
        accountTaxTypeList={accountTaxTypeList}
        handleSubmit={handleSubmit}
        fieldErrors={fieldErrors}
      />

      <Text fontSize="xl" pb={3}>
        Sync accounts from Quickbooks
      </Text>
      {accounts && (
        <List>
          {accounts.map((account) => (
            <ListItem key={account.id}>
              <HStack pb={2}>
                <ListIcon as={FaLink} color="blue.500" />
                <Text>
                  {account.number} - {account.name}
                </Text>
                <Spacer />
                <Button onClick={() => openModal(account)}>Create</Button>
              </HStack>
            </ListItem>
          ))}
        </List>
      )}
    </>
  );
};

const QuickbooksAccount = ({
  account,
  customer_id,
  loadQuickbooksAccounts,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState();
  const context = React.useContext(Context);

  const accounts = account.potential_matches.map((a) => ({
    value: a.Id,
    label: a.Name,
  }));

  const createAccount = (account_id) => {
    setLoading(true);
    axios
      .post(
        `/api/v2/create_qb_account/`,
        {
          customer_id: customer_id,
          account_id: account_id,
        },
        { headers: { "Content-Type": "application/json" } },
        { withCredentials: true }
      )
      .then((res) => {
        if (res.status < 300) {
          loadQuickbooksAccounts(customer_id, setLoading);
        } else {
          context.fireToast("Error", res.response.data.error, "error");
          setLoading(false);
        }
      })
      .catch((err) => {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Unable to link account. Please try again.";
        context.fireToast("Error", message, "error");
        setLoading(false);
      });
  };

  const linkAccount = (account_id) => {
    if (!selectedItem) {
      context.fireToast("Error", "Please select account to link.", "error");
      return;
    }

    setLoading(true);
    axios
      .post(
        `/api/v2/link_qb_account/`,
        {
          customer_id: customer_id,
          account_id: account_id,
          qb_account_id: selectedItem.value,
        },
        { headers: { "Content-Type": "application/json" } },
        { withCredentials: true }
      )
      .then((res) => {
        if (res.status < 300) {
          loadQuickbooksAccounts(customer_id, setLoading);
        } else {
          context.fireToast("Error", res.response.data.error, "error");
          setLoading(false);
        }
      })
      .catch((err) => {
        context.fireToast("Error", err.response.data.error, "error");
        setLoading(false);
      });
  };

  return (
    <ListItem key={account.id}>
      <HStack pb={2}>
        {account.match_found && <ListIcon as={FaLink} color="green.500" />}
        {!account.match_found && <ListIcon as={FaUnlink} color="red.500" />}
        <Text>
          {account.number} - {account.name}
        </Text>
        {account.match_found && account.type_mismatch && (
          <Text fontSize="sm" color="orange.500" fontWeight="bold">
            Type Mismatch
          </Text>
        )}
        <Spacer></Spacer>
        {!account.match_found && (
          <>
            {loading && <Spinner color="brand.600" />}
            <DropDown
              name="account"
              options={accounts}
              selectedValue={selectedItem ? selectedItem.value : null}
              onChange={setSelectedItem}
              w="350px"
              menuPortalTarget={document.body}
            />
            <Button onClick={() => linkAccount(account.id)}>Link</Button>
            <Button onClick={() => createAccount(account.id)}>Create</Button>
          </>
        )}
      </HStack>
      {account.match_found && account.children.length > 0 && (
        <List pl={5}>
          {account.children.map((account) => (
            <QuickbooksAccount
              key={account.id}
              account={account}
              customer_id={customer_id}
              loadQuickbooksAccounts={loadQuickbooksAccounts}
            />
          ))}
        </List>
      )}
    </ListItem>
  );
};

const ExportModal = ({ isOpen, onClose, exportTransactions, loading }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    const currentDate = new Date();

    const firstDay = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
      2
    );
    const lastDay = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );

    setStartDate(firstDay.toISOString().split("T")[0]);
    setEndDate(lastDay.toISOString().split("T")[0]);
  }, []);

  const handleStartChange = (e) => {
    const { value } = e.target;
    setStartDate(value);
  };

  const handleEndChange = (e) => {
    const { value } = e.target;
    setEndDate(value);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Export Transactions</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel>Start Date</FormLabel>
            <Input
              type="date"
              defaultValue={startDate}
              name="startDate"
              onChange={handleStartChange}
            />
          </FormControl>
          <FormControl>
            <FormLabel>End Date</FormLabel>
            <Input
              type="date"
              value={endDate}
              name="endDate"
              onChange={handleEndChange}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => exportTransactions(startDate, endDate)}
            isDisabled={loading}
          >
            {loading ? "Exporting..." : "Export"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const RenumberModal = ({
  isOpen,
  onClose,
  renumberQBAccounts,
  loading,
  accounts,
}) => {
  const [selectedAccounts, setSelectedAccounts] = useState([]);

  useEffect(() => {
    setSelectedAccounts(accounts.map((a) => a.value));
  }, [accounts]);

  const handleAccountCheck = (value, checked) => {
    let accs = selectedAccounts.slice() || [];
    if (checked) {
      accs.push(value);
    } else {
      accs = accs.filter((e) => e !== value);
    }
    setSelectedAccounts(accs);
  };

  const toggleAll = () => {
    if (selectedAccounts.length > 0) {
      setSelectedAccounts([]);
    } else {
      setSelectedAccounts(accounts.map((a) => a.value));
    }
  };

  const select4000 = () => {
    setSelectedAccounts(
      accounts
        .filter(
          (a) =>
            a.label.substring(0, 4) * 1 >= 4000 &&
            a.label.substring(0, 4) * 1 < 8000
        )
        .map((a) => a.value)
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Renumber Quickbooks accounts</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <HStack pb={3}>
            <Button size={"sm"} onClick={toggleAll}>
              Toggle All
            </Button>
            <Button size={"sm"} onClick={select4000}>
              Select P&L
            </Button>
          </HStack>
          <FormControl id="accounts">
            {/* <FormLabel>Bank Accounts</FormLabel> */}
            <VStack>
              {accounts.map((a) => {
                return (
                  <Box w="100%" key={a.value}>
                    <Checkbox
                      value={a.value}
                      isChecked={selectedAccounts?.includes(a.value)}
                      onChange={(e) =>
                        handleAccountCheck(e.target.value, e.target.checked)
                      }
                    >
                      {a.label}
                    </Checkbox>
                  </Box>
                );
              })}
            </VStack>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => renumberQBAccounts(selectedAccounts)}
            isDisabled={loading}
          >
            {loading ? "Updating..." : "Update"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const RestructureModal = ({
  isOpen,
  onClose,
  customer_id,
  restructureAccounts,
  loading,
}) => {
  const [loadingInternal, setLoadingInternal] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);

  useEffect(() => {
    axios
      .get(
        `/api/v2/restructure_qb_accounts/${customer_id}/`,
        { headers: { "Content-Type": "application/json" } },
        { withCredentials: true }
      )
      .then((res) => {
        setAccounts(res.data.detail);
        setLoadingInternal(false);
        setSelectedAccounts(res.data.detail.map((a) => a.value));
      })
      .catch((err) => console.log(err));
  }, [customer_id]);

  const handleAccountCheck = (value, checked) => {
    let accs = selectedAccounts.slice() || [];
    if (checked) {
      accs.push(value);
    } else {
      accs = accs.filter((e) => e !== value);
    }
    setSelectedAccounts(accs);
  };

  const toggleAll = () => {
    if (selectedAccounts.length > 0) {
      setSelectedAccounts([]);
    } else {
      setSelectedAccounts(accounts.map((a) => a.value));
    }
  };

  const select4000 = () => {
    setSelectedAccounts(
      accounts
        .filter(
          (a) =>
            a.label.substring(0, 4) * 1 >= 4000 &&
            a.label.substring(0, 4) * 1 < 8000
        )
        .map((a) => a.value)
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"xl"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Restructure Quickbooks accounts</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loadingInternal && <Spinner color="brand.600" />}
          {!loadingInternal && (
            <HStack pb={3}>
              <Button size={"sm"} onClick={toggleAll}>
                Toggle All
              </Button>
              <Button size={"sm"} onClick={select4000}>
                Select P&L
              </Button>
            </HStack>
          )}
          <FormControl id="accounts">
            <VStack>
              {accounts?.map((a) => {
                return (
                  <Box w="100%" key={a.value}>
                    <Checkbox
                      value={a.value}
                      isChecked={selectedAccounts?.includes(a.value)}
                      onChange={(e) =>
                        handleAccountCheck(e.target.value, e.target.checked)
                      }
                    >
                      {a.label}
                    </Checkbox>
                  </Box>
                );
              })}
            </VStack>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => restructureAccounts(selectedAccounts)}
            isDisabled={loading}
          >
            {loading ? "Updating..." : "Update"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ErrorModal = ({ isOpen, onClose, errors, jobData, customerId }) => {
  if (jobData) {
    // Display job details like SyncJobDetails
    const resultsByCustomer = jobData.results || {};
    const customerResults = resultsByCustomer[customerId] || {};

    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Sync Task Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={8} align="stretch">
              {Object.entries(customerResults).map(([taskType, taskResult]) => {
                if (!taskResult || typeof taskResult !== 'object' || !taskResult.status) return null;

                // Map activity types to descriptive labels
                let label;
                if (taskType === 'general_ledger_cdc') {
                  label = 'CDC Sync';
                } else if (taskType === 'general_ledger') {
                  label = 'General Ledger';
                } else if (taskType === 'trial_balance') {
                  label = 'Trial Balance';
                } else if (taskType === 'general_ledger_fetch') {
                  label = 'Fetch General Ledger Dump';
                } else if (taskType === 'general_ledger_build') {
                  label = 'Import General Ledger Dump';
                } else if (taskType === 'opening_balances') {
                  label = 'Opening Balances';
                } else {
                  label = taskType;
                }

                const status = taskResult.status;
                const taskErrors = taskResult.errors;

                return (
                  <Box key={taskType}>
                    <Heading size="md" mb={2}>{label}</Heading>
                    <Badge colorScheme={status === 'success' ? 'green' : status === 'failed' ? 'red' : 'gray'} mb={2} fontSize="md">
                      {status ? status.toUpperCase() : 'N/A'}
                    </Badge>

                    {status === 'failed' && taskErrors && (
                      <Box
                        mt={4}
                        p={4}
                        bg="red.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="red.200"
                        borderLeftWidth={4}
                        borderLeftColor="red.400"
                        color="red.800"
                        fontFamily="mono"
                        fontSize="md"
                        lineHeight={1.7}
                      >
                        {(() => {
                          let errorBlocks = [];
                          if (Array.isArray(taskErrors)) {
                            errorBlocks = taskErrors.map((e, idx) => {
                              if (typeof e === 'object') {
                                return (
                                  <Box key={idx} mb={2}>
                                    <Box fontWeight="bold">{e.error}</Box>
                                    {e.detail && (
                                      <Box pl={4} color="gray.700">
                                        {Array.isArray(e.detail) ? (
                                          e.detail.map((d, i) => (
                                            <Box key={i} as="div" fontSize="sm">- {d}</Box>
                                          ))
                                        ) : (
                                          <Box as="div" fontSize="sm">- {e.detail}</Box>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              }
                              return <Box key={idx} mb={2}>{String(e)}</Box>;
                            });
                          } else if (typeof taskErrors === 'string') {
                            errorBlocks = taskErrors.split(/\r?\n/).filter(line => line.trim() !== '').map((line, idx) => (
                              <Box key={idx} mb={2}>{line}</Box>
                            ));
                          }
                          return errorBlocks;
                        })()}
                      </Box>
                    )}

                    {taskResult.processed_count !== undefined && (
                      <Text fontSize="sm" color="gray.600" mt={2}>
                        Processed: {taskResult.processed_count} items
                      </Text>
                    )}
                  </Box>
                );
              })}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  // Fallback to old error display
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Sync Errors</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {Array.isArray(errors) &&
            errors.map((e, i) => (
              <Box key={i} mb={4}>
                <Text as="b" mb={2} display="block">
                  {e.error}
                </Text>
                <List spacing={1}>
                  {e.detail.map((d, i2) => (
                    <ListItem key={i2} ml={4}>
                      <ListIcon as={() => <Text>-</Text>} />
                      {d}
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          {!Array.isArray(errors) &&
            Object.entries(errors).map(([error, accounts]) => {
              return (
                <Box key={error} mb={4}>
                  <Text fontWeight={"bold"} mb={2}>
                    {error}
                  </Text>
                  <List spacing={1}>
                    {accounts.map((a, idx) => (
                      <ListItem key={idx} ml={4}>
                        <ListIcon as={() => <Text>-</Text>} />
                        {a}
                      </ListItem>
                    ))}
                  </List>
                  <Text>&nbsp;</Text>
                </Box>
              );
            })}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
